import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useParams, useSearchParams } from 'react-router';
import { useTitle } from 'react-use';
import * as yup from 'yup';

import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Form } from '@/components/ui/form';
import usePrompt from '@/hooks/usePrompt';
import useWebhooks from '@/hooks/useWebhooks';
import { getPermissions } from '@/redux/permissions/selectors';
import { setBrainId } from '@/redux/session/actions';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import {
  DEFAULT_TOOL_SETTINGS,
  INTERRUPTION_MODES,
  type InterruptionMode,
} from '@/shared/types/toolSettings';
import {
  WebhookType,
  type PreNbaWebhookTest,
  type WebhookPayload,
  type WebhookTest,
  type WebhookTestSchema,
} from '@/shared/types/webhook';
import { webhookSchema } from '@/util/validation/schemas/webhook-schemas';

import { usePublishWebhookSave } from './WebhookSaveAction';
import WebhookConfigCard from '../Webhooks/components/WebhookConfigCard';
import {
  formatTestPayload,
  parseTestSchema,
  sanitizeHeaders,
} from '../Webhooks/components/webhookJsonUtils';

type WebhookHeaderEntry = { name: string; value: string };
type HeaderCandidate = Partial<WebhookHeaderEntry> | Record<string, string>;

const DEFAULT_HEADER_ROW: WebhookHeaderEntry = { name: '', value: '' };

const webhookConfigSchema = webhookSchema.shape({
  includeHeaders: yup.boolean().default(false),
  interruption_mode: yup
    .mixed<InterruptionMode>()
    .oneOf(INTERRUPTION_MODES)
    .default(DEFAULT_TOOL_SETTINGS.interruption_mode),
  headers: yup
    .array()
    .of(
      yup
        .object()
        .shape({ name: yup.string().trim().max(255).default(''), value: yup.string().default('') })
    )
    .default([{ ...DEFAULT_HEADER_ROW }]),
  testPayload: yup.string().default(formatTestPayload()),
});

type FormValues = yup.InferType<typeof webhookConfigSchema>;

const defaultValues: FormValues = {
  name: '',
  url: '',
  token: '',
  type: WebhookType.ACTION,
  includeHeaders: false,
  interruption_mode: DEFAULT_TOOL_SETTINGS.interruption_mode,
  headers: [{ ...DEFAULT_HEADER_ROW }],
  testPayload: formatTestPayload(),
};

const toHeaderEntry = (raw: HeaderCandidate): WebhookHeaderEntry => {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_HEADER_ROW };
  }

  const record = raw as Record<string, unknown>;
  const entries = Object.entries(record).filter(([, value]) => typeof value === 'string') as Array<
    [string, string]
  >;

  const explicitName =
    typeof record.name === 'string'
      ? (record.name as string)
      : typeof record.key === 'string'
        ? (record.key as string)
        : undefined;

  const fallbackEntry = entries.find(([key]) => key !== 'value' && key !== 'name' && key !== 'key');

  const name = (explicitName ?? fallbackEntry?.[0] ?? '').trim();

  const value =
    typeof record.value === 'string'
      ? (record.value as string)
      : name && typeof record[name] === 'string'
        ? (record[name] as string)
        : (fallbackEntry?.[1] ?? '');

  return { name, value };
};

const ensureHeaderRows = (headers: HeaderCandidate[]): WebhookHeaderEntry[] => {
  const normalized = headers
    .map(toHeaderEntry)
    .map((header) => ({ name: header.name ?? '', value: header.value ?? '' }));

  if (normalized.length === 0) {
    return [{ ...DEFAULT_HEADER_ROW }];
  }

  return normalized;
};

const isWebhookType = (value: string | null): value is WebhookType =>
  value !== null && Object.values(WebhookType).includes(value as WebhookType);

const WebhookConfigPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { brainId, webhookId } = useParams();
  const [searchParams] = useSearchParams();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const resolvedWebhookId = webhookId ?? '';
  const isDraft = webhookId === 'draft';

  const typeFromQueryParam = useMemo(() => {
    const rawType = searchParams.get('type');
    return isWebhookType(rawType) ? rawType : null;
  }, [searchParams]);

  const {
    webhook,
    isWebhookLoading,
    updateWebhook,
    updateStatus,
    testWebhook,
    testStatus,
    testResults,
  } = useWebhooks(brainId ?? '', !isDraft ? resolvedWebhookId : undefined);

  const [hasSavedChanges, setHasSavedChanges] = useState(false);

  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(webhookConfigSchema),
    mode: 'onChange',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  // Derive the webhook type from the form (kept in sync via reset) so the config
  // card reflects the current selection without a separate piece of state.
  const watchedType = useWatch({ control, name: 'type' });
  const webhookType = watchedType ?? webhook?.type ?? typeFromQueryParam ?? WebhookType.ACTION;

  useTitle(t('pages.webhook', { 0: webhook?.name || t('common.draft') }));

  useSegments(PageName.WEBHOOK);

  useEffect(() => {
    if (brainId) {
      dispatch(setBrainId({ brainId }));
    }
  }, [brainId, dispatch]);

  const normalizedHeaders = useMemo(() => {
    if (!webhook?.headers) {
      return ensureHeaderRows([]);
    }

    // Handle string type (likely JSON-stringified)
    if (typeof webhook.headers === 'string') {
      try {
        const parsed = JSON.parse(webhook.headers);
        return ensureHeaderRows(Array.isArray(parsed) ? parsed : []);
      } catch {
        return ensureHeaderRows([]);
      }
    }

    // Handle array type
    return ensureHeaderRows(webhook.headers);
  }, [webhook?.headers]);

  const hasHeaderValues = useMemo(
    () =>
      normalizedHeaders.some(
        (header) => header.name?.trim()?.length || header.value?.trim()?.length
      ),
    [normalizedHeaders]
  );

  const currentTestSchema = useMemo(
    () => parseTestSchema(webhook?.test_schema),
    [webhook?.test_schema]
  );

  const savedPayload = currentTestSchema.payload ?? null;

  useEffect(() => {
    if (webhook) {
      const type = webhook.type ?? WebhookType.ACTION;
      reset({
        name: webhook.name ?? '',
        url: webhook.url ?? '',
        token: webhook.token ?? '',
        type,
        includeHeaders: hasHeaderValues,
        interruption_mode: webhook.interruption_mode ?? DEFAULT_TOOL_SETTINGS.interruption_mode,
        headers: hasHeaderValues
          ? [{ ...DEFAULT_HEADER_ROW }, ...normalizedHeaders]
          : ensureHeaderRows([]),
        testPayload: savedPayload ? JSON.stringify(savedPayload, null, 2) : formatTestPayload(type),
      });
    } else {
      reset({
        ...defaultValues,
        headers: ensureHeaderRows([]),
        testPayload: formatTestPayload(typeFromQueryParam ?? WebhookType.ACTION),
      });
    }
    setHasSavedChanges(false);
  }, [hasHeaderValues, normalizedHeaders, reset, savedPayload, typeFromQueryParam, webhook]);

  const handleRunTest = useCallback(
    (payload: WebhookTest | PreNbaWebhookTest) => {
      if (!brainId) {
        return;
      }
      testWebhook(payload);
    },
    [brainId, testWebhook]
  );

  const updateTestSchema = useCallback(
    (patch: Partial<WebhookTestSchema>) => {
      if (!canWrite || !brainId || !resolvedWebhookId) {
        return;
      }
      const nextSchema = { ...currentTestSchema, ...patch };
      updateWebhook({
        brain_id: brainId,
        webhook_id: resolvedWebhookId,
        // Orchestrator requires test_schema.context on any non-empty test_schema,
        // so default it to {} when we're persisting a payload without saved context.
        test_schema: { ...nextSchema, context: nextSchema.context ?? {} },
      });
    },
    [canWrite, brainId, resolvedWebhookId, updateWebhook, currentTestSchema]
  );

  const handleUpdateVariables = useCallback(() => {
    // Only dialog webhook results carry a context; PreNba results don't.
    if (testResults && 'context' in testResults && testResults.context) {
      updateTestSchema({ context: testResults.context });
    }
  }, [testResults, updateTestSchema]);

  const handleSavePayload = useCallback(
    (payload: WebhookPayload) => updateTestSchema({ payload }),
    [updateTestSchema]
  );

  const handleSave = useCallback(
    (values: FormValues) => {
      if (!brainId) {
        return;
      }

      const cleanedHeaders = sanitizeHeaders(values.headers ?? []);
      const payloadHeaders = values.includeHeaders ? cleanedHeaders : [];

      if (!resolvedWebhookId) {
        return;
      }

      updateWebhook(
        {
          brain_id: brainId,
          webhook_id: resolvedWebhookId,
          name: values.name,
          url: values.url,
          token: values.token,
          type: values.type,
          headers: payloadHeaders,
          ...(values.type === WebhookType.ACTION && {
            interruption_mode: values.interruption_mode,
          }),
        },
        {
          onSuccess: () => {
            setHasSavedChanges(true);
            reset({
              ...values,
              headers: [{ ...DEFAULT_HEADER_ROW }, ...payloadHeaders],
              testPayload: values.testPayload ?? defaultValues.testPayload,
            });
          },
        }
      );
    },
    [brainId, reset, resolvedWebhookId, updateWebhook]
  );

  usePublishWebhookSave({ isDirty, isSaving: updateStatus === 'pending', canWrite });

  usePrompt(
    isDirty && !hasSavedChanges,
    t('common.header_prompt'),
    undefined,
    handleSubmit(handleSave)
  );

  // Redirect to webhooks list if the webhook doesn't exist after loading completes
  if (!isDraft && !isWebhookLoading && !webhook) {
    return <Navigate to=".." replace />;
  }

  return (
    <PageContentWrapper readOnly={!canWrite}>
      {isDraft ? null : (
        <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-6 px-4 py-8">
          <Form {...form}>
            <form
              id="webhook-config-form"
              onSubmit={handleSubmit(handleSave)}
              className="flex flex-col gap-6"
            >
              <WebhookConfigCard
                className="min-h-[600px]"
                onTest={handleRunTest}
                onUpdateVariables={handleUpdateVariables}
                onSavePayload={handleSavePayload}
                isTesting={testStatus === 'pending'}
                testResults={testResults ?? null}
                isCreateMode={false}
                canWrite={canWrite}
                isSaving={updateStatus === 'pending'}
                webhookType={webhookType}
              />
            </form>
          </Form>
        </div>
      )}
    </PageContentWrapper>
  );
};

export default memo(WebhookConfigPage);
